import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, KeyRound, RefreshCw, Power, Trash2, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, useAuth } from "@/lib/auth";

export default function AdminTeamMember() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const isMaster = user?.role === "master_admin";
    const [member, setMember] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/team/${id}`);
            setMember(r.data);
        } catch (err) {
            toast.error("Member not found.");
            navigate("/admin/team");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const action = async (label, fn) => {
        setBusy(label);
        try { await fn(); toast.success(`${label} done.`); await load(); }
        catch (err) {
            const msg = err?.response?.data?.detail || `${label} failed.`;
            toast.error(typeof msg === "string" ? msg : `${label} failed.`);
        } finally { setBusy(""); }
    };

    const isMasterTarget = member?.role === "master_admin";

    if (loading || !member) {
        return <div className="py-10 text-center"><Loader2 className="mx-auto animate-spin text-mm-navy" /></div>;
    }

    return (
        <div className="space-y-5" data-testid="admin-team-member">
            <Link to="/admin/team" className="inline-flex items-center gap-1.5 text-sm text-mm-navy/80 hover:text-mm-blue">
                <ArrowLeft size={14} /> Back to team
            </Link>
            <header className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="mm-eyebrow">Member</div>
                    <h1 className="mm-display text-3xl text-mm-navy">
                        {[member.first_name, member.last_name].filter(Boolean).join(" ") || member.email}
                    </h1>
                    <p className="mt-1 text-sm text-mm-muted">
                        @{member.username} · <span className="font-mono">{member.email}</span>
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {!isMasterTarget && (
                        <>
                            <button
                                onClick={() => action("Reset password", async () => {
                                    const r = await api.post(`/admin/team/${id}/reset-password`);
                                    toast.message("Temp password issued — sent via email.", { description: `${r.data.temp_password}` });
                                })}
                                disabled={!!busy}
                                className="mm-btn-outline !py-2 text-xs"
                                data-testid="member-reset-password"
                            >
                                <KeyRound size={12} /> Reset password
                            </button>
                            <button
                                onClick={() => action("Resend welcome", async () => {
                                    const r = await api.post(`/admin/team/${id}/resend-invite`);
                                    toast.message("Welcome resent — temp password rotated.", { description: `${r.data.temp_password}` });
                                })}
                                disabled={!!busy}
                                className="mm-btn-outline !py-2 text-xs"
                                data-testid="member-resend"
                            >
                                <RefreshCw size={12} /> Resend welcome
                            </button>
                            {member.status === "inactive" ? (
                                <button
                                    onClick={() => action("Reactivate", async () => api.post(`/admin/team/${id}/activate`))}
                                    disabled={!!busy}
                                    className="mm-btn-primary !py-2 text-xs"
                                    data-testid="member-activate"
                                >
                                    <Power size={12} /> Reactivate
                                </button>
                            ) : (
                                <button
                                    onClick={() => action("Deactivate", async () => api.post(`/admin/team/${id}/deactivate`))}
                                    disabled={!!busy}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-rose-300 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-800 hover:bg-rose-100"
                                    data-testid="member-deactivate"
                                >
                                    <Power size={12} /> Deactivate
                                </button>
                            )}
                            {isMaster && (
                                <button
                                    onClick={async () => {
                                        if (!window.confirm(`Delete ${member.email}? This is a soft delete.`)) return;
                                        try {
                                            await api.delete(`/admin/team/${id}`);
                                            toast.success("Member deleted.");
                                            navigate("/admin/team");
                                        } catch (err) {
                                            toast.error(err?.response?.data?.detail || "Delete failed.");
                                        }
                                    }}
                                    disabled={!!busy}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-3 py-2 text-xs font-medium text-white hover:bg-rose-700"
                                    data-testid="member-delete"
                                >
                                    <Trash2 size={12} /> Delete
                                </button>
                            )}
                        </>
                    )}
                    {isMasterTarget && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs text-amber-900">
                            <ShieldCheck size={12} /> Master admin · permanent
                        </span>
                    )}
                </div>
            </header>

            <div className="grid gap-5 lg:grid-cols-2">
                <Card title="Profile">
                    <Row label="Status" value={member.status} />
                    <Row label="Role" value={member.role} />
                    <Row label="Phone" value={member.phone} />
                    <Row label="Start date" value={member.start_date} />
                    <Row label="Invited by" value={member.invited_by} />
                    <Row label="Invited at" value={member.invited_at && new Date(member.invited_at).toLocaleString()} />
                    <Row label="Accepted at" value={member.accepted_at && new Date(member.accepted_at).toLocaleString()} />
                    <Row label="Last login" value={member.last_login_at && new Date(member.last_login_at).toLocaleString()} />
                </Card>
                <Card title="License">
                    <Row label="License number" value={member.license_number} />
                    <Row label="States licensed" value={(member.states_licensed || []).join(", ") || "—"} />
                </Card>
            </div>

            <Card title="Activity">
                <p className="text-sm text-mm-muted">
                    Lead assignments, calls, and deal history will appear here once the round-robin engine is wired in.
                </p>
            </Card>
        </div>
    );
}

function Card({ title, children }) {
    return (
        <section className="mm-card p-5">
            <div className="mm-eyebrow mb-3">{title}</div>
            <div className="space-y-2">{children}</div>
        </section>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between gap-4 text-sm">
            <span className="text-mm-muted">{label}</span>
            <span className="text-right font-medium text-mm-ink">{value || "—"}</span>
        </div>
    );
}
